<script lang="ts">
  import { onMount } from "svelte";
  import { push } from "svelte-spa-router";
  import {
    login,
    requestVerify,
    ApiError,
    type ActiveSession,
    type SessionLimitBody,
  } from "../lib/api";
  import {
    setUser,
    setError,
    session,
    canUseAdminApp,
    deriveScopes,
    landingPathFor,
  } from "../lib/session.svelte";

  const EMAIL_KEY = "admin-last-email";

  let email = "";
  let password = "";
  let submitting = false;
  let localError: string | null = null;
  /** True when the last login failed because the email is unverified. */
  let needsVerify = false;
  let resendState: "idle" | "sending" | "sent" = "idle";

  // ---- Concurrent-session limit (HTTP 409 `session_limit`) ----
  // Backend rejects login when too many sessions are active and returns the
  // live list. We let the user kill enough of them to free a slot, then retry
  // the SAME login with kill_session_ids.
  let sessionLimitOpen = false;
  let activeSessions: ActiveSession[] = [];
  let maxSessions = 0;
  let killSelected = new Set<string>();

  // Sessions that MUST be killed to free one slot: active - max + 1 (min 1).
  $: neededKills = Math.max(1, activeSessions.length - maxSessions + 1);
  $: canConfirmKill = killSelected.size >= neededKills && !submitting;

  /** Humanize an age in ms into "2 Std. 14 Min." / "5 Min."; null ⇒ unbekannt. */
  function humanizeAge(ageMs: number | null): string {
    if (ageMs == null) return "unbekannt";
    const totalMin = Math.floor(ageMs / 60000);
    if (totalMin < 1) return "gerade eben";
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h > 0) return m > 0 ? `${h} Std. ${m} Min.` : `${h} Std.`;
    return `${m} Min.`;
  }

  /** Muted metadata line for one session row. */
  function sessionMeta(s: ActiveSession): string {
    const device =
      !s.device || s.device === "Unknown" ? "unbekanntes Gerät" : s.device;
    const browser =
      !s.browser || s.browser === "Unknown" ? "unbekannter Browser" : s.browser;
    const ip = s.ip ?? "unbekannte IP";
    return `${device} · ${browser} · ${ip} · seit ${humanizeAge(s.age_ms)} aktiv`;
  }

  function toggleKill(id: string) {
    const next = new Set(killSelected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    killSelected = next;
  }

  function cancelSessionLimit() {
    sessionLimitOpen = false;
    activeSessions = [];
    killSelected = new Set();
    submitting = false;
  }

  /** Open (or refresh, on a repeat 409) the picker from a session_limit body. */
  function openSessionLimit(body: SessionLimitBody) {
    activeSessions = body.active_sessions ?? [];
    maxSessions = body.max_sessions;
    // Preserve still-valid prior selections (the server list may have changed).
    const live = new Set(activeSessions.map((s) => s.id));
    const kept = new Set([...killSelected].filter((id) => live.has(id)));
    if (maxSessions === 1 && activeSessions.length === 1) {
      kept.add(activeSessions[0].id);
    }
    killSelected = kept;
    sessionLimitOpen = true;
  }

  /** Narrow an unknown ApiError body to a `session_limit` 409 payload. */
  function asSessionLimit(err: unknown): SessionLimitBody | null {
    if (!(err instanceof ApiError) || err.status !== 409) return null;
    const b = err.body;
    if (
      b &&
      typeof b === "object" &&
      (b as Record<string, unknown>).error === "session_limit"
    ) {
      return b as SessionLimitBody;
    }
    return null;
  }

  /** Confirm: retry login killing the selected sessions. */
  function confirmKill() {
    if (!canConfirmKill) return;
    void doLogin([...killSelected]);
  }

  function submit(e: Event) {
    e.preventDefault();
    void doLogin();
  }

  /**
   * Perform the login request and the shared post-login flow. Used by both the
   * initial submit and the post-kill retry from the session-limit picker.
   *
   * @param killIds session ids to free on a retry; omitted on the first attempt.
   */
  async function doLogin(killIds?: string[]): Promise<void> {
    localError = null;
    needsVerify = false;
    resendState = "idle";
    setError(null);
    if (!email || !password) {
      localError = "Email and password required.";
      return;
    }
    submitting = true;
    try {
      const res = await login(
        killIds && killIds.length > 0
          ? { email, password, kill_session_ids: killIds }
          : { email, password },
      );
      sessionLimitOpen = false;
      if (!canUseAdminApp(res.roles)) {
        localError = "This is the student app's account. Use the student app to sign in.";
        return;
      }
      setUser({
        email: res.email,
        display_name: res.display_name,
        roles: res.roles,
        isAdmin: res.roles.some((r) => r.role === "admin"),
        mustChangePassword: res.must_change_password === true,
      });
      // Land on a page the user's roles actually allow. Non-admins must NOT be
      // sent to the admin-only dashboard (every /admin/* call there 403s).
      try {
        localStorage.setItem(EMAIL_KEY, email);
      } catch {
        /* ignore storage errors */
      }
      push(landingPathFor(deriveScopes(res.roles)));
    } catch (err) {
      const limit = asSessionLimit(err);
      if (limit) {
        openSessionLimit(limit);
        return;
      }
      if (err instanceof ApiError && err.message === "email_not_verified") {
        needsVerify = true;
        localError = "Your email isn't verified yet. Check your inbox or resend the link.";
        setError(localError);
        return;
      }
      const msg =
        err instanceof ApiError
          ? err.status === 401
            ? "Wrong email or password."
            : err.message
          : "Login failed.";
      localError = msg;
      setError(msg);
    } finally {
      submitting = false;
    }
  }

  onMount(() => {
    try {
      const saved = localStorage.getItem(EMAIL_KEY);
      if (saved) email = saved;
    } catch {
      /* ignore storage errors */
    }
  });

  async function resend() {
    resendState = "sending";
    try {
      await requestVerify({ email });
    } catch {
      /* always show the same neutral result */
    }
    resendState = "sent";
  }
</script>

<section class="login">
  <div class="card">
    <h1>wiederdabei Admin</h1>
    <p class="sub">Sign in with your admin account.</p>
    <form on:submit={submit}>
      <label>
        Email
        <input
          type="email"
          autocomplete="username"
          bind:value={email}
          data-testid="email"
          required
        />
      </label>
      <label>
        Password
        <input
          type="password"
          autocomplete="current-password"
          bind:value={password}
          data-testid="password"
          required
        />
      </label>
      <button type="submit" disabled={submitting} data-testid="submit">
        {submitting ? "Signing in…" : "Sign in"}
      </button>
      {#if localError}
        <p class="error" data-testid="error">{localError}</p>
      {:else if $session.error}
        <p class="error">{$session.error}</p>
      {/if}
      {#if needsVerify}
        {#if resendState === "sent"}
          <p class="sub" data-testid="resend-sent">
            If your account needs verification, a new link is on its way.
          </p>
        {:else}
          <button
            type="button"
            class="reset-link"
            on:click={resend}
            disabled={resendState === "sending"}
            data-testid="resend-verification"
          >
            {resendState === "sending" ? "Sending…" : "Resend verification email"}
          </button>
        {/if}
      {/if}
    </form>
    <button
      type="button"
      class="reset-link"
      on:click={() => push("/reset")}
      data-testid="forgot-password"
    >
      Forgot password?
    </button>
  </div>
</section>

{#if sessionLimitOpen}
  <div
    class="sl-backdrop"
    role="dialog"
    aria-modal="true"
    aria-labelledby="session-limit-title"
    data-testid="session-limit-backdrop"
  >
    <div class="card sl-dialog">
      <h1 id="session-limit-title">Zu viele aktive Anmeldungen</h1>
      <p class="sub">
        Du kannst auf höchstens {maxSessions} Gerät(en) gleichzeitig angemeldet
        sein. Wähle eine oder mehrere bestehende Anmeldungen zum Beenden aus, um
        hier weiterzumachen.
      </p>
      <p class="sub" data-testid="session-limit-hint">
        Wähle mindestens {neededKills} Anmeldung(en) aus, um einen Platz frei zu
        machen.
      </p>
      <ul class="sl-list">
        {#each activeSessions as s (s.id)}
          <li>
            <label class="sl-item">
              <input
                type="checkbox"
                checked={killSelected.has(s.id)}
                on:change={() => toggleKill(s.id)}
                data-testid="session-kill-checkbox"
              />
              <span class="sl-meta">{sessionMeta(s)}</span>
            </label>
          </li>
        {/each}
      </ul>
      <div class="sl-actions">
        <button
          type="button"
          class="sl-cancel"
          on:click={cancelSessionLimit}
          disabled={submitting}
          data-testid="session-limit-cancel"
        >
          Abbrechen
        </button>
        <button
          type="button"
          on:click={confirmKill}
          disabled={!canConfirmKill}
          data-testid="session-limit-confirm"
        >
          {submitting ? "Anmelden…" : "Ausgewählte beenden & weiter"}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .login {
    min-height: 70vh;
    display: grid;
    place-items: center;
    padding: 1rem;
  }
  .card {
    width: 100%;
    max-width: 360px;
    background: #111827;
    border: 1px solid #1f2937;
    border-radius: 12px;
    padding: 1.5rem;
  }
  h1 {
    margin: 0 0 0.25rem;
    font-size: 1.25rem;
  }
  .sub {
    margin: 0 0 1rem;
    color: #9ca3af;
  }
  form {
    display: grid;
    gap: 0.75rem;
  }
  label {
    display: grid;
    gap: 0.25rem;
    font-size: 0.85rem;
    color: #d1d5db;
  }
  input {
    padding: 0.55rem 0.7rem;
    background: #0f172a;
    color: #f3f4f6;
    border: 1px solid #1f2937;
    border-radius: 6px;
  }
  input:focus {
    outline: 2px solid #10b981;
    outline-offset: 1px;
  }
  button {
    padding: 0.55rem 0.7rem;
    background: #10b981;
    color: #04221a;
    font-weight: 600;
    border: none;
    border-radius: 6px;
  }
  .error {
    margin: 0;
    color: #fca5a5;
    font-size: 0.85rem;
  }
  .reset-link {
    margin-top: 0.9rem;
    background: none;
    border: none;
    color: #9ca3af;
    font-size: 0.8rem;
    text-decoration: underline;
    cursor: pointer;
    padding: 0;
  }
  .reset-link:hover {
    color: #d1d5db;
  }
  .sl-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    display: grid;
    place-items: center;
    padding: 1rem;
    z-index: 50;
  }
  .sl-dialog {
    max-width: 460px;
  }
  .sl-list {
    list-style: none;
    margin: 0 0 1rem;
    padding: 0;
    display: grid;
    gap: 0.5rem;
  }
  .sl-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: #0f172a;
    border: 1px solid #1f2937;
    border-radius: 6px;
    padding: 0.5rem 0.7rem;
    cursor: pointer;
  }
  .sl-meta {
    color: #9ca3af;
    font-size: 0.8rem;
  }
  .sl-actions {
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
  }
  .sl-cancel {
    background: none;
    color: #d1d5db;
    border: 1px solid #1f2937;
  }
</style>
